import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = '/api';
const RECARGO_TARJETA = 0.30;
const WA_NUM = '543401418735';
const fmtARS = n => `$${Math.round(Number(n)).toLocaleString('es-AR')}`;

// ── Colores base del tema ──────────────────────────────────────────────────
const C = {
  bg950:   '#0a0a0a',
  bg900:   '#141414',
  bg800:   '#1c1c1c',
  gold300: '#dac063',
  gold400: '#cca73b',
  gold500: '#b8902d',
  gold700: '#7c561f',
  gold900: '#54391c',
  gold950: '#301e0e',
  text:    '#e5e5e5',
  muted:   '#737373',
  muted2:  '#a3a3a3',
};

const goldGradient = `linear-gradient(135deg, ${C.gold300} 0%, ${C.gold700} 100%)`;
const glassBg = 'rgba(20,20,20,0.82)';
const glassBorder = `1px solid rgba(218,192,99,0.18)`;

// ── Quiz configuración ─────────────────────────────────────────────────────
const QUIZ = [
  {
    key: 'genero',
    titulo: '¿Para quién es la fragancia?',
    opciones: [
      { valor: 'Masculino',  label: 'Masculino',       desc: 'Maderas fuertes y especias exóticas', icon: '🧔' },
      { valor: 'Femenino',   label: 'Femenino',        desc: 'Flores, vainilla y seducción',        icon: '👩' },
      { valor: 'todos',      label: 'Sin preferencia', desc: 'El equilibrio perfecto',              icon: '✨' },
      { valor: 'decants',    label: 'Decants',          desc: 'Muestras de 5ml y 10ml',             icon: '🧪' },
    ],
  },
  {
    key: 'aroma',
    titulo: '¿Qué acordes te seducen más?',
    opciones: [
      { valor: 'dulce',     label: 'Dulce & Gourmand',    desc: 'Vainilla, caramelo y coco',      icon: '🍯' },
      { valor: 'oriental',  label: 'Oud & Oriental',      desc: 'Oud, ámbar y especias',          icon: '🌙' },
      { valor: 'fresco',    label: 'Fresco & Acuático',   desc: 'Cítrico, verde y marino',        icon: '🌿' },
      { valor: 'amaderado', label: 'Amaderado',           desc: 'Cedro, sándalo y vetiver',       icon: '🪵' },
    ],
  },
  {
    key: 'momento',
    titulo: '¿Qué proyección buscás?',
    opciones: [
      { valor: 'noche',   label: 'Intensa (Beast Mode)', desc: 'Estela imponente, deja huella', icon: '👑' },
      { valor: 'dia',     label: 'Moderada / Elegante',  desc: 'Sofisticada y sutil',           icon: '🛡️' },
    ],
  },
];

const AROMA_KW = {
  dulce:     ['vainilla', 'dulce', 'caramelo', 'miel', 'gourmand', 'coco', 'praline', 'chocolate'],
  fresco:    ['fresco', 'citrico', 'bergamota', 'limon', 'naranja', 'verde', 'acuatico', 'marino'],
  oriental:  ['oud', 'ambar', 'incienso', 'especias', 'oriental', 'almizcle', 'resina'],
  amaderado: ['madera', 'cedro', 'sandalo', 'patchouli', 'vetiver', 'musgo'],
};
const MOMENTO_KW = {
  noche: ['intenso', 'oud', 'ambar', 'oriental', 'profundo', 'nocturno', 'fuerte'],
  dia:   ['fresco', 'suave', 'limpio', 'discreto', 'citrico', 'ligero', 'tenue'],
};

// ── Helpers de estilo ──────────────────────────────────────────────────────
function GlassCard({ children, style = {}, className = '', onClick }) {
  return (
    <div
      onClick={onClick}
      className={className}
      style={{
        background: glassBg,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: glassBorder,
        borderRadius: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}



// ── Tarjeta de producto ────────────────────────────────────────────────────
function ProductCard({ perfume, onVerDetalle, onAgregarCarrito }) {
  const [hover, setHover] = useState(false);
  const [added, setAdded] = useState(false);
  const precio = parseFloat(perfume.precio);
  const pf = perfume.descuento > 0 ? Math.round(precio * (1 - perfume.descuento / 100)) : precio;
  const tieneDecant = parseFloat(perfume.precio_decant_5ml) > 0 || parseFloat(perfume.precio_decant_10ml) > 0;
  const sinStock = !perfume.stock || perfume.stock <= 0;
  const precioDecantMin = Math.min(
    parseFloat(perfume.precio_decant_5ml) || Infinity,
    parseFloat(perfume.precio_decant_10ml) || Infinity
  );

  const imageBg = (() => {
    const f = (perfume.familia || '').toLowerCase();
    if (f.includes('masc') || f.includes('hombre')) return 'linear-gradient(160deg, #e8ddd0 0%, #c9b898 100%)';
    if (f.includes('fem')  || f.includes('mujer'))  return 'linear-gradient(160deg, #ede0d4 0%, #d4b8a0 100%)';
    return 'linear-gradient(160deg, #e2ddd4 0%, #c4b898 100%)';
  })();

  const handleAgregar = e => {
    e.stopPropagation();
    if (sinStock && !tieneDecant) return;
    if (sinStock && tieneDecant) { onVerDetalle(perfume); return; }
    onAgregarCarrito(perfume);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? 'rgba(28,28,28,0.95)' : 'rgba(20,20,20,0.7)',
        border: `1px solid ${hover ? 'rgba(218,192,99,0.45)' : 'rgba(218,192,99,0.12)'}`,
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s',
        transform: hover ? 'translateY(-4px)' : 'none',
        boxShadow: hover ? '0 16px 40px rgba(0,0,0,0.6)' : '0 2px 12px rgba(0,0,0,0.3)',
        position: 'relative',
      }}
    >
      {/* Imagen con degradado por familia */}
      <div
        onClick={() => onVerDetalle(perfume)}
        style={{ position: 'relative', paddingTop: '115%', background: imageBg, overflow: 'hidden' }}
      >
        <img
          src={perfume.imagen_url}
          alt={perfume.nombre}
          onError={e => { e.target.style.display = 'none'; }}
          style={{
            position: 'absolute',
            top: 8, left: 8,
            width: 'calc(100% - 16px)', height: 'calc(100% - 16px)',
            objectFit: 'contain',
            transform: hover ? 'scale(1.04)' : 'scale(1)',
            transition: 'transform 0.5s ease',
          }}
        />
        {perfume.descuento > 0 && (
          <div style={{ position: 'absolute', top: 10, left: 10, background: '#111', color: C.gold300, fontSize: 9, fontWeight: 700, padding: '3px 10px', borderRadius: 100, letterSpacing: 1.5, border: `1px solid ${C.gold700}` }}>
            -{perfume.descuento}%
          </div>
        )}
        {tieneDecant && (
          <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(201,169,110,0.9)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '3px 10px', borderRadius: 100 }}>
            🧪 Decant
          </div>
        )}
        {sinStock && !tieneDecant && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase', color: '#999', background: 'rgba(255,255,255,0.9)', padding: '5px 14px', borderRadius: 20 }}>Sin stock</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '14px 14px 0', flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }} onClick={() => onVerDetalle(perfume)}>
        <span style={{ fontSize: 9, color: C.gold400, letterSpacing: '0.25em', textTransform: 'uppercase', fontWeight: 600 }}>
          {perfume.marca || ''}
        </span>
        <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 17, fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: 0 }}>
          {perfume.nombre}
        </h3>
      </div>

      {/* Precio + Agregar */}
      <div style={{ padding: '12px 14px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid rgba(218,192,99,0.08)`, marginTop: 10 }}>
        <div>
          <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 20, fontWeight: 700, color: sinStock && !tieneDecant ? C.muted : C.gold300 }}>
            {sinStock && tieneDecant ? `desde ${fmtARS(precioDecantMin)}` : fmtARS(pf)}
          </span>
          {perfume.descuento > 0 && !sinStock && (
            <span style={{ fontSize: 11, color: C.muted, textDecoration: 'line-through', marginLeft: 6 }}>
              {fmtARS(precio)}
            </span>
          )}
        </div>
        <button
          onClick={handleAgregar}
          disabled={sinStock && !tieneDecant}
          title="Añadir al carrito"
          style={{
            background: added ? '#22a06b' : sinStock ? (tieneDecant ? 'rgba(218,192,99,0.25)' : 'rgba(255,255,255,0.04)') : goldGradient,
            border: `1px solid ${added ? '#22a06b' : 'rgba(218,192,99,0.4)'}`,
            borderRadius: '50%',
            color: added ? '#fff' : sinStock && !tieneDecant ? C.muted : C.bg950,
            width: 36, height: 36,
            cursor: sinStock && !tieneDecant ? 'not-allowed' : 'pointer',
            fontSize: added ? 14 : 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s',
            transform: added ? 'scale(1.15)' : 'scale(1)',
            flexShrink: 0,
          }}
        >
          {added ? '✓' : sinStock && tieneDecant ? '🧪' : '+'}
        </button>
      </div>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────
export default function Catalogo() {
  const [perfumes, setPerfumes]         = useState([]);
  const [cargando, setCargando]         = useState(true);
  const [buscar, setBuscar]             = useState('');
  const [filtroGenero, setFiltroGenero] = useState([]);
  const [filtroMarca, setFiltroMarca]   = useState([]);
  const [filtroTipo, setFiltroTipo]     = useState([]);
  const [orden, setOrden]               = useState('default');
  const [carrito, setCarrito]           = useState([]);
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [quizPaso, setQuizPaso]         = useState(0);
  const [quizRespuestas, setQuizRespuestas] = useState({});
  const [quizResultado, setQuizResultado]   = useState(null);
  const [menuMobile, setMenuMobile]     = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/perfumes`)
      .then(r => { setPerfumes(r.data); setCargando(false); })
      .catch(() => setCargando(false));
  }, []);

  useEffect(() => {
    if (!sessionStorage.getItem('ep_visit')) {
      sessionStorage.setItem('ep_visit', '1');
      fetch(`${API}/visitas`, { method: 'POST' }).catch(() => {});
    }
  }, []);

  // ── Carrito ──────────────────────────────────────────────────────────────
  const agregarAlCarrito = p => {
    setCarrito(prev => {
      const found = prev.find(x => x.id === p.id);
      if (found) return prev.map(x => x.id === p.id ? { ...x, qty: x.qty + 1 } : x);
      return [...prev, { ...p, qty: 1 }];
    });
    setCarritoAbierto(true);
  };

  const cambiarCantidad = (id, qty) => {
    if (qty <= 0) setCarrito(prev => prev.filter(x => x.id !== id));
    else setCarrito(prev => prev.map(x => x.id === id ? { ...x, qty } : x));
  };

  const totalCarrito = carrito.reduce((a, x) => {
    const p = parseFloat(x.precio);
    return a + (x.descuento > 0 ? Math.round(p * (1 - x.descuento / 100)) : p) * x.qty;
  }, 0);

  const enviarPedidoWA = () => {
    const lineas = carrito.map(x => {
      const p = parseFloat(x.precio);
      const pf = x.descuento > 0 ? Math.round(p * (1 - x.descuento / 100)) : p;
      return `• ${x.qty}x ${x.nombre} - ${fmtARS(pf * x.qty)}`;
    }).join('\n');
    const msg = `Hola! Me gustaría consultar por:\n\n${lineas}\n\nTotal efectivo: ${fmtARS(totalCarrito)}\nCon tarjeta (${Math.round(RECARGO_TARJETA * 100)}% recargo): ${fmtARS(Math.round(totalCarrito * (1 + RECARGO_TARJETA)))}\n\nessencepure.carpell.com.ar`;
    const items = carrito.map(x => {
      const p = parseFloat(x.precio);
      const pf = x.descuento > 0 ? Math.round(p * (1 - x.descuento / 100)) : p;
      return { id: x.id, nombre: x.nombre, cantidad: x.qty, precio: pf };
    });
    fetch('/api/ventas/publica', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, total: totalCarrito, notas: 'Pedido carrito web' }),
    }).catch(() => {});
    window.open(`https://wa.me/${WA_NUM}?text=${encodeURIComponent(msg)}`, '_blank');
    setCarrito([]);
    setCarritoAbierto(false);
  };

  // ── Quiz ─────────────────────────────────────────────────────────────────
  const responderQuiz = (key, valor) => {
    const nuevas = { ...quizRespuestas, [key]: valor };
    setQuizRespuestas(nuevas);
    if (key === 'genero' && valor === 'decants') {
      const res = perfumes.filter(p => p.stock > 0 && (parseFloat(p.precio_decant_5ml) > 0 || parseFloat(p.precio_decant_10ml) > 0)).slice(0, 4);
      setQuizResultado(res.length > 0 ? res : perfumes.filter(p => p.stock > 0).slice(0, 4));
      setQuizPaso(3);
      return;
    }
    if (quizPaso < QUIZ.length - 1) {
      setQuizPaso(p => p + 1);
    } else {
      if (nuevas.genero === 'decants') {
        const res = perfumes.filter(p => p.stock > 0 && (parseFloat(p.precio_decant_5ml) > 0 || parseFloat(p.precio_decant_10ml) > 0)).slice(0, 4);
        setQuizResultado(res.length > 0 ? res : perfumes.filter(p => p.stock > 0).slice(0, 4));
        setQuizPaso(3);
        return;
      }
      const scored = perfumes.map(p => {
        const txt = ((p.notas || '') + ' ' + (p.descripcion || '') + ' ' + (p.familia || '')).toLowerCase();
        let score = 0;
        if (nuevas.genero && nuevas.genero !== 'todos') {
          if (p.familia === nuevas.genero) score += 5; else score -= 2;
        }
        (AROMA_KW[nuevas.aroma] || []).forEach(kw => { if (txt.includes(kw)) score += 3; });
        (MOMENTO_KW[nuevas.momento] || []).forEach(kw => { if (txt.includes(kw)) score += 2; });
        return { ...p, score };
      });
      const res = scored.filter(p => p.stock > 0 && p.score > 0).sort((a, b) => b.score - a.score).slice(0, 4);
      setQuizResultado(res.length > 0 ? res : perfumes.filter(p => p.stock > 0).slice(0, 4));
      setQuizPaso(3);
    }
  };

  const resetQuiz = () => { setQuizPaso(0); setQuizRespuestas({}); setQuizResultado(null); };

  // ── Filtros ──────────────────────────────────────────────────────────────
  const marcas = useMemo(() => [...new Set(perfumes.map(p => p.marca).filter(Boolean))].sort(), [perfumes]);

  const toggle = (setter, val) => setter(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const filtrados = useMemo(() => {
    let lista = perfumes.filter(p => {
      if (!p.stock || p.stock <= 0) return false;
      const mt = !buscar || p.nombre.toLowerCase().includes(buscar.toLowerCase()) || (p.marca || '').toLowerCase().includes(buscar.toLowerCase());
      const mg = filtroGenero.length === 0 || filtroGenero.includes(p.familia);
      const mm = filtroMarca.length === 0 || filtroMarca.includes(p.marca);
      const hd = parseFloat(p.precio_decant_5ml) > 0 || parseFloat(p.precio_decant_10ml) > 0;
      const mTipo = filtroTipo.length === 0 || filtroTipo.some(t =>
        (t === 'entero' && parseFloat(p.precio) > 0 && !hd) ||
        (t === 'entero-decant' && parseFloat(p.precio) > 0 && hd) ||
        (t === 'decant' && hd)
      );
      return mt && mg && mm && mTipo;
    });
    if (orden === 'price-asc')  lista = [...lista].sort((a, b) => parseFloat(a.precio) - parseFloat(b.precio));
    if (orden === 'price-desc') lista = [...lista].sort((a, b) => parseFloat(b.precio) - parseFloat(a.precio));
    if (orden === 'rating')     lista = [...lista].sort((a, b) => (b.descuento || 0) - (a.descuento || 0));
    return lista;
  }, [perfumes, buscar, filtroGenero, filtroMarca, filtroTipo, orden]);

  const limpiarFiltros = () => { setBuscar(''); setFiltroGenero([]); setFiltroMarca([]); setFiltroTipo([]); setOrden('default'); };

  const cantCarrito = carrito.reduce((a, x) => a + x.qty, 0);

  // ─────────────────────────────── RENDER ───────────────────────────────────
  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", background: C.bg950, color: C.text, minHeight: '100vh' }}>

      {/* ── ESTILOS GLOBALES ─────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,400&family=Inter:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0a0a0a; }
        ::-webkit-scrollbar-thumb { background: #3a3a3a; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #7c561f; }
        @keyframes ep-pulse { 0%,100% { opacity:1 } 50% { opacity:0.5 } }
        @keyframes ep-shimmer { 0% { transform:translateX(-100%) } 100% { transform:translateX(100%) } }
        input[type=checkbox] { accent-color: #cca73b; }
        .ep-card-hover:hover { transform: translateY(-4px); }
      `}</style>

      {/* ══════════════════════ NAVBAR ══════════════════════════════════ */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: glassBg,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(218,192,99,0.15)',
        padding: '14px 24px',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>

          {/* Logo */}
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <img src="/logo.png" alt="Essence Pure" onError={e => { e.target.style.display='none'; }}
              style={{ height: 38, filter: 'brightness(0) invert(1)', mixBlendMode: 'normal' }} />
            <div>
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 700, letterSpacing: '0.2em', color: C.gold300, display: 'block', lineHeight: 1 }}>ESSENCE PURE</span>
              <span style={{ fontSize: 8, letterSpacing: '0.3em', color: C.muted, textTransform: 'uppercase', display: 'block' }}>Alta Perfumería</span>
            </div>
          </a>

          {/* Links desktop */}
          <div style={{ display: 'flex', gap: 28, fontFamily: 'Cormorant Garamond, serif', fontSize: 17 }}>
            {[['#catalogo', 'Catálogo'], ['#quiz-olfativo', '✨ Asistente'], ['#experiencia', 'La Tradición']].map(([href, label]) => (
              <a key={href} href={href} style={{ color: C.muted2, textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = C.gold300}
                onMouseLeave={e => e.target.style.color = C.muted2}
              >{label}</a>
            ))}
          </div>

          {/* Search + Cart */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative', display: window.innerWidth < 640 ? 'none' : 'block' }}>
              <input
                type="text"
                placeholder="Buscar fragancia..."
                value={buscar}
                onChange={e => setBuscar(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(218,192,99,0.2)',
                  borderRadius: 100, padding: '7px 16px 7px 36px', fontSize: 12, color: C.text,
                  outline: 'none', width: 180, transition: 'width 0.3s',
                }}
                onFocus={e => e.target.style.width = '240px'}
                onBlur={e => e.target.style.width = '180px'}
              />
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: C.muted, pointerEvents: 'none' }}>🔍</span>
            </div>

            <button
              onClick={() => setCarritoAbierto(true)}
              style={{ position: 'relative', background: 'none', border: '1px solid rgba(218,192,99,0.25)', borderRadius: '50%', width: 42, height: 42, cursor: 'pointer', color: C.gold300, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              🛍
              {cantCarrito > 0 && (
                <span style={{ position: 'absolute', top: -4, right: -4, background: goldGradient, color: C.bg950, fontSize: 9, fontWeight: 700, width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {cantCarrito}
                </span>
              )}
            </button>

            <button onClick={() => setMenuMobile(v => !v)}
              style={{ background: 'none', border: 'none', color: C.gold300, fontSize: 22, cursor: 'pointer', display: window.innerWidth >= 768 ? 'none' : 'block' }}>
              ☰
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuMobile && (
        <div style={{ background: 'rgba(10,10,10,0.97)', borderBottom: glassBorder, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'Cormorant Garamond, serif', fontSize: 20, textAlign: 'center', zIndex: 49, position: 'relative' }}>
          {[['#catalogo', 'Catálogo'], ['#quiz-olfativo', '✨ Asistente Olfativo'], ['#experiencia', 'La Tradición']].map(([href, label]) => (
            <a key={href} href={href} onClick={() => setMenuMobile(false)} style={{ color: C.muted2, textDecoration: 'none', padding: '8px 0', borderBottom: '1px solid rgba(218,192,99,0.1)' }}>{label}</a>
          ))}
          <input type="text" placeholder="Buscar fragancia..." value={buscar} onChange={e => setBuscar(e.target.value)}
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(218,192,99,0.25)', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: C.text, outline: 'none', marginTop: 8 }} />
        </div>
      )}

      {/* ══════════════════════ HERO ════════════════════════════════════ */}
      <header style={{ position: 'relative', minHeight: '88vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '60px 24px' }}>
        {/* Gradiente de fondo */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, #1c1410 0%, #0a0a0a 70%)', zIndex: 0 }} />
        {/* Blur dorado izq */}
        <div style={{ position: 'absolute', top: '15%', left: '10%', width: 480, height: 480, borderRadius: '50%', background: C.gold950, filter: 'blur(130px)', opacity: 0.18, pointerEvents: 'none' }} />
        {/* Blur dorado der */}
        <div style={{ position: 'absolute', bottom: '15%', right: '10%', width: 480, height: 480, borderRadius: '50%', background: '#4a1d00', filter: 'blur(130px)', opacity: 0.14, pointerEvents: 'none' }} />
        {/* Textura sutil */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)', backgroundSize: '8px 8px', pointerEvents: 'none' }} />

        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, maxWidth: 900 }}>
          <span style={{ color: C.gold400, letterSpacing: '0.45em', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 20, animation: 'ep-pulse 3s infinite' }}>
            Especialistas en Alta Perfumería Árabe
          </span>

          <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(44px, 8.5vw, 100px)', fontWeight: 700, color: '#fff', lineHeight: 1.08, margin: '0 0 28px' }}>
            El Arte Olfativo del<br />
            <span style={{ background: `linear-gradient(135deg, ${C.gold300} 0%, #f0c040 40%, ${C.gold700} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textShadow: 'none' }}>
              Imperio Árabe
            </span>
          </h1>

          <p style={{ color: C.muted2, maxWidth: 580, margin: '0 auto 44px', lineHeight: 1.85, fontSize: 15, fontWeight: 300 }}>
            Fragancias majestuosas con Oud, rosas y resinas milenarias de fijación extrema.
            Fragancias importadas de origen árabe.
          </p>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#catalogo" style={{ padding: '14px 32px', background: goldGradient, color: C.bg950, fontWeight: 700, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', borderRadius: 100, textDecoration: 'none' }}>
              Explorar Colección
            </a>
            <a href="#quiz-olfativo" style={{ padding: '14px 32px', background: 'rgba(255,255,255,0.05)', color: C.gold300, border: `1px solid rgba(218,192,99,0.35)`, fontWeight: 600, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', borderRadius: 100, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
              ✨ Encontrar mi Perfume
            </a>
          </div>

          {/* Métricas */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, maxWidth: 720, margin: '72px auto 0', paddingTop: 40, borderTop: '1px solid rgba(218,192,99,0.1)' }}>
            {[['100%', 'Original'], ['+18h', 'Fijación Extrema'], ['Gratis', 'Envío Nacional'], ['3 cuotas', 'Sin Intereses']].map(([num, label], i) => (
              <div key={i} style={{ textAlign: 'center', borderLeft: i > 0 ? '1px solid rgba(218,192,99,0.1)' : 'none', paddingLeft: i > 0 ? 12 : 0 }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 700, color: C.gold300, display: 'block' }}>{num}</span>
                <span style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.2em' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ══════════════════════ QUIZ OLFATIVO ═══════════════════════════ */}
      <section id="quiz-olfativo" style={{ padding: '80px 24px', background: 'rgba(20,15,8,0.6)', borderTop: '1px solid rgba(218,192,99,0.08)', borderBottom: '1px solid rgba(218,192,99,0.08)', position: 'relative' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 18, color: C.gold400, display: 'block', marginBottom: 6 }}>¿Indeciso/a sobre cuál elegir?</span>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(28px,5vw,46px)', fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>Asistente Olfativo Personal</h2>
            <p style={{ color: C.muted2, fontSize: 13, margin: 0 }}>Respondé 3 preguntas rápidas y te recomendamos el elixir ideal según tus gustos.</p>
          </div>

          <GlassCard style={{ padding: '32px 28px' }}>
            {/* Barra de progreso */}
            <div style={{ background: '#1f1f1f', height: 5, borderRadius: 100, marginBottom: 32, overflow: 'hidden' }}>
              <div style={{ background: goldGradient, height: '100%', borderRadius: 100, width: quizPaso >= 3 ? '100%' : `${((quizPaso + 1) / QUIZ.length) * 100}%`, transition: 'width 0.5s' }} />
            </div>

            {/* Pasos del quiz */}
            {quizPaso < 3 && QUIZ[quizPaso] && (
              <div>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#fff', textAlign: 'center', marginBottom: 24 }}>
                  {quizPaso + 1}. {QUIZ[quizPaso].titulo}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: QUIZ[quizPaso].opciones.length <= 3 ? `repeat(${QUIZ[quizPaso].opciones.length}, 1fr)` : 'repeat(2, 1fr)', gap: 12 }}>
                  {QUIZ[quizPaso].opciones.map(op => (
                    <button
                      key={op.valor}
                      onClick={() => responderQuiz(QUIZ[quizPaso].key, op.valor)}
                      style={{
                        padding: '18px 14px', background: 'rgba(10,10,10,0.7)', borderRadius: 14,
                        border: '1px solid rgba(218,192,99,0.18)', cursor: 'pointer', textAlign: 'center',
                        transition: 'all 0.25s', color: C.text,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold400; e.currentTarget.style.background = 'rgba(218,192,99,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(218,192,99,0.18)'; e.currentTarget.style.background = 'rgba(10,10,10,0.7)'; }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{op.icon}</div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 4 }}>{op.label}</div>
                      <div style={{ fontSize: 11, color: C.muted2 }}>{op.desc}</div>
                    </button>
                  ))}
                </div>
                {quizPaso > 0 && (
                  <button onClick={() => setQuizPaso(p => p - 1)} style={{ marginTop: 16, background: 'none', border: 'none', color: C.gold400, cursor: 'pointer', fontSize: 12, display: 'block', margin: '16px auto 0', textDecoration: 'underline' }}>
                    ← Atrás
                  </button>
                )}
              </div>
            )}

            {/* Resultados */}
            {quizPaso === 3 && quizResultado && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 8 }}>¡Tu elixir ideal está listo!</h3>
                <p style={{ color: C.muted2, fontSize: 13, marginBottom: 28 }}>
                  Basado en tus preferencias — <strong style={{ color: C.gold300 }}>{quizRespuestas.genero !== 'todos' ? quizRespuestas.genero : 'Unisex'}</strong>, acorde <strong style={{ color: C.gold300 }}>{quizRespuestas.aroma}</strong>
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 14, maxWidth: 560, margin: '0 auto 28px', textAlign: 'left' }}>
                  {quizResultado.slice(0, 4).map(p => {
                    const pf = p.descuento > 0 ? Math.round(parseFloat(p.precio) * (1 - p.descuento / 100)) : parseFloat(p.precio);
                    return (
                      <div key={p.id} style={{ background: 'rgba(10,10,10,0.8)', border: '1px solid rgba(218,192,99,0.3)', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setSeleccionado(p)}>
                        <img src={p.imagen_url} alt={p.nombre} onError={e => { e.target.style.display='none'; }} style={{ width: '100%', height: 120, objectFit: 'cover' }} />
                        <div style={{ padding: '10px 12px' }}>
                          <div style={{ fontSize: 9, color: C.gold400, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 3 }}>{p.marca}</div>
                          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.2, marginBottom: 6 }}>{p.nombre}</div>
                          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 15, color: C.gold300, fontWeight: 700 }}>{fmtARS(pf)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={resetQuiz} style={{ background: 'none', border: 'none', color: C.gold400, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>Volver a empezar</button>
                  <a href="#catalogo" style={{ color: C.gold300, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Ver todo el catálogo →</a>
                </div>
              </div>
            )}
          </GlassCard>
        </div>
      </section>

      {/* ══════════════════════ CATÁLOGO ════════════════════════════════ */}
      <main id="catalogo" style={{ maxWidth: 1320, margin: '0 auto', padding: '72px 24px' }}>
        {/* Encabezado */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, borderBottom: '1px solid rgba(218,192,99,0.15)', paddingBottom: 28, marginBottom: 36, flexWrap: 'wrap' }}>
          <div>
            <span style={{ color: C.gold400, textTransform: 'uppercase', letterSpacing: '0.3em', fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 6 }}>Elixires en Stock</span>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(28px,5vw,48px)', fontWeight: 700, color: '#fff', margin: 0 }}>Catálogo de Joyas</h2>
            <p style={{ color: C.muted2, fontSize: 13, margin: '6px 0 0' }}>{filtrados.length} fragancias disponibles</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(218,192,99,0.2)', borderRadius: 10, padding: '8px 14px' }}>
            <label style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>Ordenar:</label>
            <select value={orden} onChange={e => setOrden(e.target.value)}
              style={{ background: 'transparent', color: C.text, border: 'none', fontSize: 12, cursor: 'pointer', outline: 'none', appearance: 'none' }}>
              <option value="default" style={{ background: '#1a1a1a' }}>Destacados</option>
              <option value="price-asc" style={{ background: '#1a1a1a' }}>Menor precio</option>
              <option value="price-desc" style={{ background: '#1a1a1a' }}>Mayor precio</option>
              <option value="rating" style={{ background: '#1a1a1a' }}>Mayor descuento</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 32, alignItems: 'start' }}>

          {/* ── Sidebar filtros ── */}
          <aside style={{ position: 'sticky', top: 90 }}>
            {/* Búsqueda */}
            <div style={{ background: 'rgba(20,20,20,0.7)', border: glassBorder, borderRadius: 14, padding: '18px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                🔍 Buscar
              </div>
              <input type="text" placeholder="Ej: Oud, Khamrah, Vainilla..." value={buscar} onChange={e => setBuscar(e.target.value)}
                style={{ width: '100%', background: 'rgba(10,10,10,0.7)', border: '1px solid rgba(218,192,99,0.2)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.text, outline: 'none' }} />
            </div>

            {/* Marca */}
            {marcas.length > 0 && (
              <div style={{ background: 'rgba(20,20,20,0.7)', border: glassBorder, borderRadius: 14, padding: '18px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12 }}>🏷️ Marca</div>
                {marcas.map(marca => (
                  <label key={marca} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8, fontSize: 13, color: filtroMarca.includes(marca) ? C.gold300 : C.muted2 }}>
                    <input type="checkbox" checked={filtroMarca.includes(marca)} onChange={() => toggle(setFiltroMarca, marca)} />
                    {marca}
                  </label>
                ))}
              </div>
            )}

            {/* Género */}
            <div style={{ background: 'rgba(20,20,20,0.7)', border: glassBorder, borderRadius: 14, padding: '18px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12 }}>👤 Género</div>
              {['Femenino', 'Masculino', 'Unisex'].map(g => (
                <label key={g} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8, fontSize: 13, color: filtroGenero.includes(g) ? C.gold300 : C.muted2 }}>
                  <input type="checkbox" checked={filtroGenero.includes(g)} onChange={() => toggle(setFiltroGenero, g)} />
                  {g}
                </label>
              ))}
            </div>

            {/* ── Tipo de producto ── */}
            <div style={{ background: 'rgba(20,20,20,0.7)', border: glassBorder, borderRadius: 14, padding: '18px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12 }}>📦 Tipo de producto</div>
              {[['entero', 'Perfume completo'], ['entero-decant', 'Entero + Decant'], ['decant', 'Solo Decants 🧪']].map(([val, label]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8, fontSize: 13, color: filtroTipo.includes(val) ? C.gold300 : C.muted2 }}>
                  <input type="checkbox" checked={filtroTipo.includes(val)} onChange={() => toggle(setFiltroTipo, val)} />
                  {label}
                </label>
              ))}
            </div>

            <button onClick={limpiarFiltros} style={{ width: '100%', padding: '10px', background: 'none', border: '1px dashed rgba(218,192,99,0.35)', borderRadius: 10, color: C.gold400, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
              Limpiar filtros
            </button>
          </aside>

          {/* ── Grilla de productos ── */}
          <section>
            {cargando && (
              <div style={{ textAlign: 'center', padding: 80, color: C.muted, letterSpacing: '0.3em', fontSize: 11, textTransform: 'uppercase' }}>
                Cargando catálogo...
              </div>
            )}
            {!cargando && filtrados.length === 0 && (
              <div style={{ textAlign: 'center', padding: 80 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>😞</div>
                <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 24, color: '#fff', fontWeight: 600 }}>No se encontraron fragancias</h4>
                <p style={{ color: C.muted2, fontSize: 13 }}>Probá ajustando los filtros de búsqueda.</p>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20 }}>
              {filtrados.map(p => (
                <ProductCard key={p.id} perfume={p} onVerDetalle={setSeleccionado} onAgregarCarrito={agregarAlCarrito} />
              ))}
            </div>
          </section>
        </div>
      </main>

      {/* ══════════════════════ LA TRADICIÓN ════════════════════════════ */}
      <section id="experiencia" style={{ padding: '80px 24px', background: 'linear-gradient(to top, #0a0a0a, rgba(20,14,6,0.8))', borderTop: '1px solid rgba(218,192,99,0.08)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div>
            <span style={{ color: C.gold400, textTransform: 'uppercase', letterSpacing: '0.3em', fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 12 }}>La Gran Herencia</span>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(28px,4.5vw,46px)', fontWeight: 700, color: '#fff', lineHeight: 1.15, marginBottom: 20 }}>
              ¿Por qué elegir Perfumes Árabes?
            </h2>
            <p style={{ color: '#a3a3a3', fontSize: 14, lineHeight: 1.9, fontWeight: 300, marginBottom: 32 }}>
              A diferencia de la perfumería occidental, los elixires de Oriente Medio se formulan con alta concentración de aceites puros y materias primas sagradas como el Oud y el Ámbar.
            </p>
            {[
              ['🌟', 'El Codiciado Oro Negro: Oud', 'Extraído del árbol de Aquilaria, ofrece una nota resinosa, terrosa y sensual de extrema persistencia.'],
              ['🔥', 'Fijación de Más de 18 Horas', 'Su alta pureza y base alcohólica controlada garantizan que la fragancia madure en la piel con intensidad todo el día.'],
              ['👑', 'Presentación Majestuosa', 'Cada botella es una obra de arte: metales pulidos, grabados árabes y cristales facetados dignos de coleccionistas.'],
            ].map(([icon, title, desc]) => (
              <div key={title} style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <div style={{ background: 'rgba(124,86,31,0.2)', border: '1px solid rgba(218,192,99,0.2)', borderRadius: 10, padding: '8px 10px', fontSize: 18, flexShrink: 0, height: 44, display: 'flex', alignItems: 'center' }}>
                  {icon}
                </div>
                <div>
                  <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 17, color: '#fff', fontWeight: 600, margin: '0 0 4px' }}>{title}</h4>
                  <p style={{ fontSize: 12, color: C.muted2, margin: 0, lineHeight: 1.7 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Decoración */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GlassCard style={{ padding: 40, maxWidth: 340, width: '100%', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, background: goldGradient, opacity: 0.04, borderRadius: 16 }} />
              <svg viewBox="0 0 100 150" style={{ width: 140, height: 196, color: C.gold400, margin: '0 auto 20px', display: 'block', filter: `drop-shadow(0 0 20px ${C.gold900})` }}>
                <path d="M40 5 L60 5 L55 25 L45 25 Z" fill="currentColor"/>
                <circle cx="50" cy="5" r="4" fill="#FFE885"/>
                <path d="M45 25 L55 25 L58 40 L42 40 Z" fill={C.gold700}/>
                <rect x="46" y="40" width="8" height="15" fill="currentColor"/>
                <path d="M30 55 L70 55 L85 90 L75 130 L25 130 L15 90 Z" fill="none" stroke="currentColor" strokeWidth="2.5"/>
                <path d="M50 55 L50 130" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
                <path d="M30 55 L50 90 L70 55" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M15 90 L50 110 L85 90" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M25 130 L50 90 L75 130" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M20 95 L80 95 L72 125 L28 125 Z" fill={C.gold300} opacity="0.15"/>
              </svg>
              <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: C.gold300, fontWeight: 600, letterSpacing: '0.1em', margin: '0 0 10px' }}>Elixir de los Reyes</h4>
              <p style={{ fontSize: 12, color: C.muted2 }}>Importado desde las principales casas perfumistas árabes.</p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* ══════════════════════ FOOTER ══════════════════════════════════ */}
      <footer id="contacto" style={{ borderTop: '1px solid rgba(218,192,99,0.1)', background: C.bg950, padding: '64px 24px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40, marginBottom: 48 }}>
          {/* Col 1 */}
          <div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 24, fontWeight: 700, color: C.gold300, letterSpacing: '0.2em', marginBottom: 4 }}>ESSENCE PURE</div>
            <div style={{ fontSize: 9, color: C.muted, letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 16 }}>Alta Perfumería</div>
            <p style={{ fontSize: 12, color: C.muted2, lineHeight: 1.8, fontWeight: 300 }}>
              Especialistas en fragancias árabes e internacionales de lujo. Aromas que capturan la realeza y la persistencia de Oriente Medio.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {[['🐦', 'https://www.instagram.com/essencepurecp'], ['📘', '#'], ['▶️', '#']].map(([icon, href]) => (
                <a key={icon} href={href} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(218,192,99,0.2)', borderRadius: '50%', color: C.gold400, textDecoration: 'none', fontSize: 14 }}>{icon}</a>
              ))}
            </div>
          </div>
          {/* Col 2 */}
          <div>
            <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: '#fff', fontWeight: 700, marginBottom: 16, letterSpacing: '0.1em' }}>Explorar</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[['#catalogo', 'Ver Catálogo Completo'], ['#quiz-olfativo', 'Asistente Olfativo'], ['#experiencia', '¿Qué es el Oud?']].map(([href, label]) => (
                <li key={href}><a href={href} style={{ color: C.muted2, textDecoration: 'none', fontSize: 13 }}>{label}</a></li>
              ))}
            </ul>
          </div>
          {/* Col 3 */}
          <div>
            <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: '#fff', fontWeight: 700, marginBottom: 16, letterSpacing: '0.1em' }}>Compromiso Premium</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12, color: C.muted2, lineHeight: 1.7 }}>
              <li>• 100% fragancias originales</li>
              <li>• Fijación garantizada 12h+</li>
              <li>• Devolución en 10 días</li>
              <li>• Envíos con seguimiento</li>
            </ul>
          </div>
          {/* Col 4 */}
          <div>
            <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: '#fff', fontWeight: 700, marginBottom: 16, letterSpacing: '0.1em' }}>Contacto</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: C.muted2 }}>
              <li>📷 @essenncepurecp</li>
              <li>📱 <a href="tel:+543401418735" style={{ color: C.muted2, textDecoration: 'none' }}>3401 41-8735</a></li>
              <li>📱 <a href="tel:+543401644076" style={{ color: C.muted2, textDecoration: 'none' }}>3401 644076</a></li>
              <li><a href="https://wa.me/543401418735" target="_blank" rel="noreferrer" style={{ color: C.gold400, fontWeight: 600, textDecoration: 'none' }}>💬 WhatsApp</a></li>
            </ul>
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(218,192,99,0.08)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, fontSize: 11, color: C.muted }}>
          <span>© 2026 Essence Pure. Todos los derechos reservados.</span>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="/" style={{ color: C.muted, textDecoration: 'none' }}>Privacidad</a>
            <a href="/" style={{ color: C.muted, textDecoration: 'none' }}>Términos</a>
          </div>
        </div>
      </footer>

      {/* ══════════════════════ CARRITO LATERAL ═════════════════════════ */}
      {/* Overlay */}
      {carritoAbierto && (
        <div onClick={() => setCarritoAbierto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 60 }} />
      )}

      {/* Sidebar */}
      <div style={{
        position: 'fixed', inset: 0, right: 0, top: 0, bottom: 0,
        left: 'auto', width: '100%', maxWidth: 440,
        background: C.bg900, borderLeft: glassBorder,
        zIndex: 70, display: 'flex', flexDirection: 'column',
        transform: carritoAbierto ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94)',
        boxShadow: '-10px 0 40px rgba(0,0,0,0.6)',
      }}>
        {/* Header carrito */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(218,192,99,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 24, fontWeight: 700, color: '#fff', margin: 0 }}>
            🛍 Cofre de Compras
          </h3>
          <button onClick={() => setCarritoAbierto(false)} style={{ background: 'none', border: 'none', color: C.muted2, fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {carrito.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 20 }}>🛒</div>
              <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#fff', fontWeight: 600, marginBottom: 8 }}>Tu cofre está vacío</h4>
              <p style={{ color: C.muted2, fontSize: 13, marginBottom: 24 }}>Explorá el catálogo y añadí tu primer elixir.</p>
              <a href="#catalogo" onClick={() => setCarritoAbierto(false)} style={{ padding: '12px 28px', background: goldGradient, color: C.bg950, fontWeight: 700, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', borderRadius: 100, textDecoration: 'none' }}>
                Ver Catálogo
              </a>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {carrito.map(item => {
                const precio = parseFloat(item.precio);
                const pf = item.descuento > 0 ? Math.round(precio * (1 - item.descuento / 100)) : precio;
                return (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(218,192,99,0.1)', borderRadius: 12, padding: 12, position: 'relative' }}>
                    <img src={item.imagen_url} alt={item.nombre} onError={e => { e.target.style.display='none'; }}
                      style={{ width: 60, height: 70, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, color: C.gold400, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 2 }}>{item.marca}</div>
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 8, lineHeight: 1.2 }}>{item.nombre}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: C.gold300, fontFamily: 'Cormorant Garamond, serif', fontSize: 16, fontWeight: 700 }}>{fmtARS(pf)}</span>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden' }}>
                          <button onClick={() => cambiarCantidad(item.id, item.qty - 1)} style={{ width: 28, height: 28, background: 'none', border: 'none', color: C.muted2, cursor: 'pointer', fontSize: 14 }}>−</button>
                          <span style={{ width: 28, textAlign: 'center', fontSize: 12, color: '#fff', fontWeight: 600 }}>{item.qty}</span>
                          <button onClick={() => cambiarCantidad(item.id, item.qty + 1)} style={{ width: 28, height: 28, background: 'none', border: 'none', color: C.muted2, cursor: 'pointer', fontSize: 14 }}>+</button>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => cambiarCantidad(item.id, 0)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 13 }}>🗑</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer carrito */}
        {carrito.length > 0 && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid rgba(218,192,99,0.1)', background: 'rgba(255,255,255,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: C.muted2 }}>
              <span>Subtotal:</span><span style={{ color: '#fff' }}>{fmtARS(totalCarrito)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, color: C.muted2 }}>
              <span>Envío:</span><span style={{ color: C.gold400, fontWeight: 700, fontSize: 11, letterSpacing: '0.1em' }}>¡GRATIS!</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 20, color: C.muted, paddingTop: 8, borderTop: '1px solid rgba(218,192,99,0.08)' }}>
              <span>Con tarjeta ({Math.round(RECARGO_TARJETA * 100)}% recargo):</span>
              <span>{fmtARS(Math.round(totalCarrito * (1 + RECARGO_TARJETA)))}</span>
            </div>
            <button onClick={enviarPedidoWA} style={{ width: '100%', padding: '14px', background: goldGradient, color: C.bg950, fontWeight: 700, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', border: 'none', borderRadius: 10, cursor: 'pointer' }}>
              💬 Consultar por WhatsApp
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════ MODAL PRODUCTO ══════════════════════════ */}
      {seleccionado && (
        <>
          <div onClick={() => setSeleccionado(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 80 }} />
          <div style={{
            position: 'fixed', inset: '16px', margin: 'auto',
            maxWidth: 900, maxHeight: '90vh',
            background: C.bg900, border: glassBorder, borderRadius: 20,
            zIndex: 90, overflowY: 'auto',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
            boxShadow: '0 30px 80px rgba(0,0,0,0.8)',
          }}>
            {/* Cierre */}
            <button onClick={() => setSeleccionado(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: C.muted2, cursor: 'pointer', fontSize: 18, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>

            {/* Imagen */}
            <div style={{ background: '#111', borderRadius: '20px 0 0 20px', position: 'relative', minHeight: 360, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(218,192,99,0.05), transparent)' }} />
              <img src={seleccionado.imagen_url} alt={seleccionado.nombre}
                onError={e => { e.target.style.display='none'; }}
                style={{ width: '85%', height: '85%', objectFit: 'contain', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 10px 30px rgba(218,192,99,0.2))' }} />
            </div>

            {/* Info */}
            <div style={{ padding: '36px 32px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
              <div>
                <span style={{ fontSize: 10, color: C.gold400, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                  {seleccionado.marca || ''}
                </span>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 700, color: '#fff', margin: '0 0 10px', lineHeight: 1.1 }}>
                  {seleccionado.nombre}
                </h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '4px 12px', background: 'rgba(218,192,99,0.12)', border: '1px solid rgba(218,192,99,0.3)', color: C.gold300, borderRadius: 100, fontWeight: 500 }}>
                    {seleccionado.familia || 'Unisex'}
                  </span>
                  {seleccionado.origen && (
                    <span style={{ fontSize: 11, padding: '4px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: C.muted2, borderRadius: 100 }}>
                      📍 {seleccionado.origen}
                    </span>
                  )}
                  {seleccionado.descuento > 0 && (
                    <span style={{ fontSize: 11, padding: '4px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 100, fontWeight: 700 }}>
                      -{seleccionado.descuento}% OFF
                    </span>
                  )}
                </div>
              </div>

              {seleccionado.descripcion && (
                <p style={{ fontSize: 13, color: C.muted2, lineHeight: 1.8, margin: 0, fontWeight: 300 }}>{seleccionado.descripcion}</p>
              )}

              {/* Notas olfativas */}
              {seleccionado.notas && (
                <div style={{ background: 'rgba(10,10,10,0.6)', border: '1px solid rgba(218,192,99,0.1)', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontSize: 11, color: C.gold300, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    🌸 Pirámide Olfativa
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {seleccionado.notas.split(',').map(n => n.trim()).filter(Boolean).map(nota => (
                      <span key={nota} style={{ fontSize: 11, padding: '4px 10px', background: 'rgba(218,192,99,0.08)', border: '1px solid rgba(218,192,99,0.2)', color: C.gold300, borderRadius: 100 }}>
                        {nota}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Decants */}
              {(parseFloat(seleccionado.precio_decant_5ml) > 0 || parseFloat(seleccionado.precio_decant_10ml) > 0) && (
                <div style={{ background: 'rgba(10,10,10,0.6)', border: '1px solid rgba(218,192,99,0.1)', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontSize: 11, color: C.gold300, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10 }}>🧪 Decants disponibles</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {parseFloat(seleccionado.precio_decant_5ml) > 0 && (
                      <button onClick={() => { agregarAlCarrito({ ...seleccionado, nombre: seleccionado.nombre + ' (Decant 5ml)', precio: seleccionado.precio_decant_5ml, descuento: 0 }); setSeleccionado(null); }}
                        style={{ flex: 1, padding: '10px', background: 'rgba(218,192,99,0.1)', border: '1px solid rgba(218,192,99,0.3)', borderRadius: 10, color: C.gold300, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        5ml · {fmtARS(seleccionado.precio_decant_5ml)}
                      </button>
                    )}
                    {parseFloat(seleccionado.precio_decant_10ml) > 0 && (
                      <button onClick={() => { agregarAlCarrito({ ...seleccionado, nombre: seleccionado.nombre + ' (Decant 10ml)', precio: seleccionado.precio_decant_10ml, descuento: 0 }); setSeleccionado(null); }}
                        style={{ flex: 1, padding: '10px', background: 'rgba(218,192,99,0.1)', border: '1px solid rgba(218,192,99,0.3)', borderRadius: 10, color: C.gold300, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        10ml · {fmtARS(seleccionado.precio_decant_10ml)}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Precio + CTA */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(218,192,99,0.1)', paddingTop: 20, marginTop: 'auto', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <span style={{ fontSize: 10, color: C.muted, display: 'block', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Precio exclusivo</span>
                  <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, fontWeight: 700, color: C.gold300 }}>
                    {(() => {
                      const p = parseFloat(seleccionado.precio);
                      return fmtARS(seleccionado.descuento > 0 ? Math.round(p * (1 - seleccionado.descuento / 100)) : p);
                    })()}
                  </span>
                  {seleccionado.descuento > 0 && (
                    <span style={{ fontSize: 13, color: C.muted, textDecoration: 'line-through', marginLeft: 8 }}>
                      {fmtARS(parseFloat(seleccionado.precio))}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => { agregarAlCarrito(seleccionado); setSeleccionado(null); }}
                  style={{ padding: '14px 28px', background: goldGradient, color: C.bg950, fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', border: 'none', borderRadius: 10, cursor: 'pointer' }}>
                  🛒 Añadir al Cofre
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
